// src/utils/dateFormat.js
// Central, timezone-safe date handling for the admin app.
//
// Two distinct kinds of dates exist in Crewboard:
//   1. CALENDAR dates (CDL expiry, Proof-of-Work expiry) — stored as "MM/DD/YYYY"
//      or "YYYY-MM-DD" strings. These are wall-clock dates with NO timezone; a
//      CDL that expires "Jun 26" expires on the 26th everywhere. They must be
//      parsed in LOCAL time, never UTC, or they shift a day in Canadian zones.
//   2. TIMESTAMPS (createdAt, verifiedAt, …) — Firestore Timestamps; absolute
//      moments. Use .toDate(); display in the viewer's local time (correct).
//
// Display format across the app: "JUN 1, 2026".

const MONTHS = ['JAN','FEB','MAR','APR','MAY','JUN','JUL','AUG','SEP','OCT','NOV','DEC'];

/**
 * parseDateLocal — turn any stored date value into a JS Date in LOCAL time.
 * Calendar strings ("MM/DD/YYYY" / "YYYY-MM-DD") are parsed as local midnight so
 * they never shift across timezones. Timestamps/Dates pass through.
 * Returns null when it can't parse.
 */
export function parseDateLocal(value) {
  if (!value) return null;

  // Firestore Timestamp
  if (value?.toDate) return value.toDate();
  // Firestore Timestamp-like { seconds }
  if (typeof value?.seconds === 'number') return new Date(value.seconds * 1000);
  // JS Date
  if (value instanceof Date) return isNaN(value) ? null : value;

  if (typeof value === 'string') {
    // MM/DD/YYYY (DatePicker output)
    if (value.includes('/')) {
      const [m, d, y] = value.split('/').map(Number);
      if (!m || !d || !y) return null;
      return new Date(y, m - 1, d); // local midnight
    }
    // ISO datetime string (has time component) — let Date parse the instant
    if (value.includes('T')) {
      const d = new Date(value);
      return isNaN(d) ? null : d;
    }
    // Bare YYYY-MM-DD — parse as LOCAL midnight (NOT UTC)
    if (value.includes('-')) {
      const [y, m, d] = value.split('-').map(Number);
      if (!y || !m || !d) return null;
      return new Date(y, m - 1, d);
    }
  }
  return null;
}

/**
 * formatDate — "JUN 1, 2026" for any date value. fallback when empty/invalid.
 */
export function formatDate(value, fallback = '—') {
  const d = parseDateLocal(value);
  if (!d) return fallback;
  return `${MONTHS[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
}

/**
 * formatDateTime — "JUN 1, 2026, 3:45 PM" for timestamps (uses local time).
 */
export function formatDateTime(value, fallback = '—') {
  const d = parseDateLocal(value);
  if (!d) return fallback;
  const date = `${MONTHS[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
  const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  return `${date}, ${time}`;
}

/**
 * daysUntil — whole calendar days from local "today" to a date value.
 * Negative if the date is already past. Returns null if unparseable.
 * Both sides are normalised to local midnight so the result is a clean day count.
 */
export function daysUntil(value) {
  const target = parseDateLocal(value);
  if (!target) return null;
  const t = new Date(target.getFullYear(), target.getMonth(), target.getDate());
  const today = new Date();
  const todayMid = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  return Math.round((t - todayMid) / 86400000);
}